import { Tooltip } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as CloudUploadIcon } from "components/Icons/cloudUploadIcon.svg";
import { ReactComponent as RemoveIcon } from "components/Icons/removeIcon.svg";

import { OLTheme } from "contexts/Theme/ThemeProvider";
import React from "react";
import Dropzone, { Accept } from "react-dropzone";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    dragDropInput: {
      alignItems: "center",
      background: theme.gray50,
      border: `1px solid ${theme.gray100}`,
      borderRadius: theme.spacing(2),
      color: theme.gray500,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: theme.spacing(20, 10),
      textAlign: "center",
    },
    dragDropFiles: {
      alignItems: "center",
      justifyContent: "space-between",
      background: theme.white00,
      border: `1px solid ${theme.gray100}`,
      borderRadius: theme.spacing(2),
      display: "flex",
      flexDirection: "row",
      marginTop: theme.spacing(8),
      overflow: "hidden",
      width: "100%",
      padding: theme.spacing(8),
      "& div": {
        display: "flex",
        alignItems: "center",
      },
    },
    removeIcon: { cursor: "pointer" },
    dragDropHeader: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: theme.spacing(13),
      textTransform: "uppercase",
      margin: theme.spacing(8, 0),
      color: theme.gray800,
    },
    helperText: {
      color: theme.gray400,
      display: "block",
      fontSize: 12,
      marginBottom: theme.spacing(13),
      marginTop: theme.spacing(2),
      paddingLeft: theme.spacing(13),
      width: "100%",
    },
    dragDropFilesWrapper: {
      display: "flex",
      alignSelf: "start",
      flexDirection: "row",
      flexWrap: "wrap",
    },
    thumb: {
      maxHeight: "100%",
      maxWidth: "100px",
      margin: theme.spacing(0, 6),
    },
    dragDropSquare: {
      "& > div": {
        width: "250px",
        height: "250px",
      },
    },
    fileName: {
      cursor: "pointer",
      marginLeft: theme.spacing(8),
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "260px",
    },
    shorterFileName: {
      maxWidth: "230px",
    },
    squareWrapperWidth: {
      maxWidth: "250px",
    },
    errorIcon: {
      color: theme.red500,
      margin: theme.spacing(0, 6),
      height: "28px",
      width: "28px",
    },
    error: {
      backgroundColor: theme.red50,
      borderColor: theme.red500,
    },
    titleWrapper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start !important",
    },
    fileSize: {
      fontSize: 14,
      fontWeight: 400,
      color: theme.black,
      opacity: 0.6,
    },
  }),
);
export interface File {
  path: string;
  preview?: string;
  size?: number;
}
type Props = {
  error?: boolean;
  caption?: React.ReactElement;
  className?: string;
  description?: React.ReactElement;
  disabled?: boolean;
  header?: string;
  inputClassName?: string;
  singleFile?: boolean;
  square?: boolean;
  onChange?: (acceptedFiles: any[], rejectedFiles: any[]) => void;
  value?: File[];
  onDelete?: () => void;
  "data-test-id"?: string;
  accept?: Accept;
};
export function DragDropInput({
  error,
  caption,
  className = "",
  description,
  disabled,
  header = "",
  inputClassName = "",
  singleFile,
  square,
  onChange,
  value,
  onDelete,
  "data-test-id": dataTestId,
  accept,
}: Props) {
  const classes = useStyles();

  return (
    <div className={className}>
      <div>
        <div className={`${square ? classes.dragDropSquare : ""}`}>
          <Dropzone
            disabled={disabled}
            multiple={!singleFile}
            onDrop={onChange}
            accept={accept}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                data-test-id={dataTestId && `${dataTestId}-drag-drop-input`}
                className={`${classes.dragDropInput} ${inputClassName}`}
                {...getRootProps()}
              >
                <input
                  {...getInputProps()}
                  data-test-id={dataTestId && dataTestId}
                />
                <CloudUploadIcon />
                {header && (
                  <span className={classes.dragDropHeader}>{header}</span>
                )}
                {description}
              </div>
            )}
          </Dropzone>
        </div>
        <div
          className={`${classes.dragDropFilesWrapper} ${
            square ? classes.squareWrapperWidth : ""
          }`}
        >
          {value &&
            value.map((item) => (
              <div
                className={`${classes.dragDropFiles} ${
                  error ? classes.error : ""
                }`}
                key={item.path}
              >
                <div>
                  <CloudUploadIcon />
                  {item.preview ? (
                    <img
                      alt={item.path}
                      className={classes.thumb}
                      src={item.preview}
                      title={item.path}
                    />
                  ) : (
                    <Tooltip
                      title={item.path}
                      className={`${classes.fileName} ${
                        error ? classes.shorterFileName : ""
                      }`}
                    >
                      <div className={classes.titleWrapper}>
                        <span>{item.path}</span>
                        {item.size && (
                          <span className={classes.fileSize}>
                            {Math.ceil(item.size / 1000)}kb
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  )}
                </div>
                <div>
                  {error && (
                    <AlertTriangleIcon
                      data-test-id="import-error-icon"
                      className={classes.errorIcon}
                      fontSize="medium"
                    />
                  )}
                  {!disabled && (
                    <RemoveIcon
                      className={classes.removeIcon}
                      onClick={() => onDelete?.()}
                    />
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
      {caption && <div className={classes.helperText}>{caption}</div>}
    </div>
  );
}
